import React, { useEffect, useState } from 'react';
import { ReactComponent as PartsNotFoundImage } from 'assets/images/parts-not-found.svg';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import useHttp from 'utils/hooks/useHttp';
import { useSearchParams } from 'react-router-dom';
import { IBodyResponseOil } from 'interfaces/oil';
import { IOilCatalogFilter } from 'utils/hooks/http/oils-catalog.http';

import OilNavbar from 'components/navbar/oil-navbar/OilNavbar';
import ProductsViewMode from 'components/ProductsViewMode/ProductsViewMode';
import { DEFAULT_VIEW } from 'components/ProductsViewMode/ProductsViewMode.constants';
import ProductsViewModeChanger from 'components/ProductsViewModeChanger/ProductsViewModeChanger';
import {
  CATALOG_OIL
} from 'components/ProductsViewModeChanger/products-view-mode-changer-utils';
import List from 'components/List/List';
import CatalogOilFilter from '../CatalogOilFilter/CatalogOilFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { ICategory } from 'views/Sidebar/Sidebar.model';
import './CatalogOilsMobile.scss';
import { sortByOptions } from 'components/SortBy/SortBy.map';
import SortBy, { SortByParams } from 'components/SortBy/SortBy';
import useReliableCatalogCarQueryParams from 'utils/hooks/useReliableCatalogCarQueryParams';

const CatalogOilsMobile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const http = useHttp();

  const [loading, setLoading] = useState(true);
  const [refData, setRefData] = useState<IBodyResponseOil>();
  const [filterData, setFilterData] = useState<IOilCatalogFilter>();
  const [openFilter, setOpenFilter] = useState(false);
  const [oilCategories, setOilCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [isCategorySelected, setIsCategorySelected] = useState(false);
  const user = JSON.parse(localStorage.getItem('userData')!);
  const [viewMode, setViewMode] = useState(user?.user_settings.products_view_mode || DEFAULT_VIEW);
  const [activeCategoryInfo, setActiveCategoryInfo] = useState<ICategory>();
  const [totalCount, setTotalCount] = useState(0);
  const reliableSearchParams = useReliableCatalogCarQueryParams();

  function getOilData(data) {
    setLoading(true);
    setRefData(undefined);
    const sortBySelectedOption = sortByOptions.filter((item) => String(item.value) == String(searchParams.get('sort')))[0] ?? [];

    const sortParams: SortByParams = {
      sort_by_column: String(searchParams.get('sort') ?? ''),
      sort_by_asc: String(searchParams.get('asc') ?? sortBySelectedOption?.default_sort_asc ?? ''),
    };

    const params = {
      ...data,
      ...sortParams,
      page,
    };

    const validParams = validateParams(params);

    http.oilCatalogHttp
      .getOils(validParams)
      .then((res) => {
        setRefData(res.data);
      })
      .finally(() => setLoading(false));
  }

  function getOilFilter(data) {

    const validFilterParams = validateParams(data);

    http.oilCatalogHttp.getOilFilters(validFilterParams).then((res) => {
      setFilterData(res.data);
    });
  }

  const validateParams = (data) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});
  };

  function getOilCategories() {
    setLoadingCategories(true);
    http.catalogs.getCarOilAllCategories({}).then((res) => {
      setOilCategories(res);
      setTotalCount(res.reduce((total, item) => total + Number(item.number_of_products), 0));
    }).finally(() => setLoadingCategories(false));
  }

  const handlePageClick = (pageN: number) => {
    setPage(pageN);
  };


  useEffect(() => {
    const params = {
      q: searchParams.get('q'),
      category_ids: searchParams.getAll('category_id'),
      brand_id: searchParams.getAll('brand_id'),
      viscosity: searchParams.getAll('viscosity'),
      quantity: searchParams.getAll('quantity'),
      family: searchParams.getAll('family')
    };

    if (searchParams.get('category_id')) {
      getOilData(params);
    }
  }, [
    page,
    searchParams.get('category_id'),
    searchParams.get('brand_id'),
    searchParams.get('viscosity'),
    searchParams.get('quantity'),
    searchParams.get('family'),
    searchParams.get('sort'),
    searchParams.get('asc'),
  ]);


  useEffect(() => {
    const params = {
      q: searchParams.get('q'),
      category_ids: searchParams.getAll('category_id'),
      brand_id: searchParams.getAll('brand_id'),
      viscosity: searchParams.getAll('viscosity'),
      quantity: searchParams.getAll('quantity'),
      family: searchParams.getAll('family')
    };

    if (searchParams.get('category_id')) {
      getOilFilter(params);
    }
  }, [searchParams.get('category_id')]);

  const onCategoryClick = (category) => {
    setActiveCategoryInfo(category);
    setIsCategorySelected(true);
    setSearchParams({ category_id: [category.id] });
    setPage(1);
  };

  useEffect(() => {
    getOilCategories();
  }, []);


  function filterOptionsElement() {
    if (!searchParams.get('category_id')) {
      return <div className="catalog-parts-filter"></div>;
    }

    return (
      <div className="catalog-parts-filter">
        <button onClick={() => setOpenFilter(true)} className={`catalog-parts-button ${openFilter ? 'active' : ''}`}>
          {t('_filter_results_btn')}
        </button>
      </div>
    );
  }

  const onClickReturn = () => {
    setSearchParams({ ...reliableSearchParams });
    setIsCategorySelected(false);
  };

  return (
    <div className="catalog-oils-wrapper-mobile">
      <OilNavbar option={filterOptionsElement()} title="_oil_catalog" />
      <div className="separator"></div>

      <div className="filterOptions">
        {refData?.data?.length && refData?.data?.length > 0 && searchParams.get('category_id') ? <SortBy /> : null}
        <div className="results">
          {!isCategorySelected && totalCount ? totalCount + ' ' + t('_results') : (refData?.data?.length && refData?.data?.length > 0 ? refData?.total_count + ' ' + t('_results') : (loading ? <Spinner class="extra-small" /> : null))}
        </div>
        <ProductsViewMode viewMode={viewMode} setViewMode={setViewMode} />
      </div>

      {isCategorySelected ? (
        <div className="oils-catalog-heading">
          <div className="oils-category-return" onClick={() => onClickReturn()}><FontAwesomeIcon icon={faCircleArrowLeft} />{t('_return')}</div>
          <div className="oils-category-title">{activeCategoryInfo?.name}</div>
        </div>
      ) : null}

      <div className="retailer-catalog-oil-container-mobile">
        {!isCategorySelected ? (
          <div className={`catalog-oil-sidebar-mobile ${loadingCategories ? 'loading' : null}`}>
            {loadingCategories ? <Spinner class="small" /> : (
              oilCategories && oilCategories.length ? (
                <List
                  content={oilCategories?.map((item) => {
                    return { ...item, id: item.id, name: item.name };
                  })}
                  images={oilCategories?.map((item) => item.image)}
                  itemClassName="parts-list-catalog-item"
                  itemId="id"
                  onItemClick={onCategoryClick}

                />
              ) : null
            )}
          </div>
        ) : (
          <div className="wrapperItems">
            {loading ? (
              <Spinner class="car-parts-spinner" />
            ) : refData?.data?.length ? (
              <ProductsViewModeChanger catalog={CATALOG_OIL} viewMode={viewMode} data={refData?.data} />
            ) : (
              <div className="no-parts-data">
                <div className="notFoundSvg">
                  <PartsNotFoundImage />
                </div>
                <h1>{t('_oils_not_found')}</h1>
              </div>
            )}
          </div>
        )}
      </div>
      {(refData && refData?.total_pages && refData?.page && refData?.total_pages > 1) && isCategorySelected ? (
        <Pagination pageCount={refData.total_pages} handlePageClick={handlePageClick} forcePage={refData.page} />
      ) : (
        null
      )}
      <CatalogOilFilter getData={getOilData} filterData={filterData} openFilter={openFilter} setOpenFilter={setOpenFilter} />
    </div>
  );
};

export default CatalogOilsMobile;
